// Unified domain-aware API base resolution for guest-frontend
// Rules:
// 1. Respect REACT_APP_API_URL if it is public (not localhost/private IP)
// 2. If current host is a custom domain (gala.|guest.|api.) map to api.<root>/api
// 3. Local dev fallback uses :5001 for dev backend
// 4. Service backend assumed at service.<domain> or :5500 locally

function resolveApiBase() {
  const host = (typeof window !== 'undefined' && window.location?.hostname) || 'localhost';
  const proto = (typeof window !== 'undefined' && window.location?.protocol) || 'http:';
  const isIp = /^(\d{1,3}\.){3}\d{1,3}$/.test(host);

  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG?.API_URL) {
    return window.__RUNTIME_CONFIG.API_URL;
  }

  // Domain/IP-based mapping when not on localhost
  if (host && host !== 'localhost' && host !== '127.0.0.1') {
    // Cloud Run sibling service derivation: <service>-<hash>-uc.a.run.app
    const crMatch = host.match(/-([a-z0-9]{6,})-uc\.a\.run\.app$/i);
    if (crMatch) {
      const parts = host.split('-');
      const hash = parts[parts.length - 2];
      const candidate = `${proto}//backend-${hash}-uc.a.run.app/api`;
      if (typeof window !== 'undefined') {
        // eslint-disable-next-line no-console
        console.log('[guest-frontend] Derived API candidate', candidate, 'from host', host);
        window.__GUEST_API_DEBUG = { host, candidate };
      }
      return candidate;
    }
    if (/-frontend-[a-z0-9]{4,}-uc\.a\.run\.app$/i.test(host)) {
      const hash = host.split('-').slice(-2)[0];
      const candidate = `${proto}//backend-${hash}-uc.a.run.app/api`;
      if (typeof window !== 'undefined') {
        // eslint-disable-next-line no-console
        console.log('[guest-frontend] Fallback backend candidate', candidate);
      }
      return candidate;
    }
    // If numeric IP, use explicit port mapping
    if (isIp) return `${proto}//${host}:5000/api`;
    return `${proto}//${host}/api`;
  }
  // Local dev fallback defaults to dev backend port 5000 if env var not provided
  return `${proto}//${host}:5000/api`;
}

function resolveServiceBase() {
  const host = (typeof window !== 'undefined' && window.location?.hostname) || 'localhost';
  const proto = (typeof window !== 'undefined' && window.location?.protocol) || 'http:';
  const isIp = /^(\d{1,3}\.){3}\d{1,3}$/.test(host);

  // Check runtime config first
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG?.SERVICE_URL) {
    return window.__RUNTIME_CONFIG.SERVICE_URL;
  }

  if (host && host !== 'localhost' && host !== '127.0.0.1') {
    const crMatch = host.match(/-([a-z0-9]{6,})-uc\.a\.run\.app$/i);
    if (crMatch) {
      const parts = host.split('-');
      const hash = parts[parts.length - 2];
      return `${proto}//service-backend-${hash}-uc.a.run.app/api/service`;
    }
    if (/-frontend-[a-z0-9]{4,}-uc\.a\.run\.app$/i.test(host)) {
      const hash = host.split('-').slice(-2)[0];
      return `${proto}//service-backend-${hash}-uc.a.run.app/api/service`;
    }
    if (isIp) return `${proto}//${host}:5500/api/service`;
    if (/^service\./i.test(host)) return `${proto}//${host}/api/service`;
    // For meamsynergy.com domains (checkin, guest, etc.), use same-origin path to avoid SSL cert issues
    if (/\.meamsynergy\.com$/i.test(host)) {
      console.log('[guest-frontend] Using same-origin SERVICE_URL for meamsynergy.com:', '/api/service');
      return '/api/service';
    }
    // If current host is gala.|guest.|api., strip prefix and add service.
    const root = host.replace(/^(gala|guest|checkin|api)\./i, '');
    return `${proto}//service.${root}/api/service`;
  }
  return `${proto}//${host}:5500/api/service`;
}

export const API_BASE_URL = resolveApiBase();
// Use the resolved service base URL directly
export const SERVICE_BASE_URL = resolveServiceBase();

// Optional debug (non-production)
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.log('[guest-frontend] API_BASE_URL:', API_BASE_URL, 'SERVICE_BASE_URL:', SERVICE_BASE_URL);
}
